<template>
  <div class="auth-container">
    <div class="auth-tabs">
      <button 
        v-if="!auth.isLoggedIn"
        class="auth-tab"
        :class="{ active: activeTab === 'login' }"
        @click="setActiveTab('login')"
      >
        登录
      </button>
      <button 
        v-if="!auth.isLoggedIn"
        class="auth-tab"
        :class="{ active: activeTab === 'signup' }"
        @click="setActiveTab('signup')"
      >
        注册
      </button>
      <button 
        v-if="auth.isLoggedIn"
        class="auth-tab"
        :class="{ active: activeTab === 'password' }"
        @click="setActiveTab('password')"
      >
        修改密码
      </button>
    </div>

    <div class="auth-form">
      <!-- Login Form -->
      <form v-if="activeTab === 'login' && !auth.isLoggedIn" @submit.prevent="handleLogin" class="auth-form-content">
        <h2>用户登录</h2>
        <div class="form-group">
          <input 
            v-model="loginData.username" 
            type="text" 
            placeholder="用户名" 
            required
            class="form-input"
          />
        </div>
        <div class="form-group">
          <input 
            v-model="loginData.password" 
            type="password" 
            placeholder="密码" 
            required
            class="form-input"
          />
        </div>
        <button type="submit" class="button button-primary">
          登录
        </button>
        <p v-if="loginError" class="error-message">{{ loginError }}</p>
      </form>

      <!-- Signup Form -->
      <form v-if="activeTab === 'signup' && !auth.isLoggedIn" @submit.prevent="handleSignup" class="auth-form-content">
        <h2>新用户注册</h2>
        <div class="form-group">
          <input 
            v-model="signupData.username" 
            type="text" 
            placeholder="用户名" 
            required
            class="form-input"
          />
        </div>
        <div class="form-group">
          <input 
            v-model="signupData.password" 
            type="password" 
            placeholder="密码" 
            required
            minlength="6"
            class="form-input"
          />
        </div>
        <div class="form-group">
          <input 
            v-model="signupData.confirmPassword" 
            type="password" 
            placeholder="确认密码" 
            required
            class="form-input"
          />
        </div>
        <button type="submit" class="button button-primary">
          注册
        </button>
        <p v-if="signupError" class="error-message">{{ signupError }}</p>
      </form>

      <!-- Password Change Form -->
      <form v-if="activeTab === 'password' && auth.isLoggedIn" @submit.prevent="handleChangePassword" class="auth-form-content">
        <h2>修改密码</h2>
        <div class="form-group">
          <input 
            v-model="passwordData.currentPassword" 
            type="password" 
            placeholder="当前密码" 
            required
            class="form-input"
          />
        </div>
        <div class="form-group">
          <input 
            v-model="passwordData.newPassword" 
            type="password" 
            placeholder="新密码" 
            required
            minlength="6"
            class="form-input"
          />
        </div>
        <div class="form-group">
          <input 
            v-model="passwordData.confirmPassword" 
            type="password" 
            placeholder="确认新密码" 
            required
            class="form-input"
          />
        </div>
        <button type="submit" class="button button-primary">
          更新密码
        </button>
        <p v-if="passwordError" class="error-message">{{ passwordError }}</p>
        <p v-if="passwordSuccess" class="success-message">{{ passwordSuccess }}</p>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useMutation } from '@vue/apollo-composable';
import { useAuthStore } from '@/stores/auth';
import { LOGIN, SIGNUP, CHANGE_PASSWORD } from '../graphql/queries';


const router = useRouter();

// Pinia Store
const auth = useAuthStore();

// Form data
const userSelectedTab = ref(null);
const loginData = ref({ username: '', password: '' });
const signupData = ref({ username: '', password: '', confirmPassword: '' });
const passwordData = ref({ currentPassword: '', newPassword: '', confirmPassword: '' });

// Computed
const defaultTab = computed(() => auth.isLoggedIn ? 'password' : 'login');
const activeTab = computed(() => userSelectedTab.value || defaultTab.value);

// Status 
const loginError = ref('');
const signupError = ref('');
const passwordError = ref('');
const passwordSuccess = ref('');

// Method
const setActiveTab = (tab) => {
  // allow user to choose between login and signup tabs when not logged in
  if(!auth.isLoggedIn){
    userSelectedTab.value = tab;
  }
};

// Reset userSelectedTab when logging out
watch(() => auth.isLoggedIn, (isLoggedIn) => {
  if (!isLoggedIn) {
    userSelectedTab.value = null;
  }
});

// Login mutation
const { mutate: login, onDone: onLoginDone, onError: onLoginError } = useMutation(LOGIN);

onLoginDone((result) => {
  console.log("login successful");
  const { token, user } = result.data?.login;
  auth.login( token, user );
  const redirectUrl = localStorage.getItem('redirectUrl') || '/';
  localStorage.removeItem('redirectUrl');
  router.push(redirectUrl);
});

onLoginError((err) => {
  console.error("login error", err);
  loginData.value.password = loginData.value.username = '';
  loginError.value = err.message || '登录失败，请检查用户名和密码';
});

// Signup mutation
const { mutate: signup, onDone: onSignupDone, onError: onSignupError } = useMutation(SIGNUP);

onSignupDone(() => {
  console.log("signup successful");

  // auto-login after signup
  login({
    input: {
      username: signupData.value.username,
      password: signupData.value.password
    }
  });
});

onSignupError((err) => {
  signupError.value = err.message || '注册失败，请稍后再试';
  signupData.value.password = signupData.value.username = signupData.value.confirmPassword = '';
});

// Change password mutation
const { mutate: changePassword, onDone: onChangePasswordDone, onError: onChangePasswordError } = useMutation(CHANGE_PASSWORD);

onChangePasswordDone(() => {
  passwordSuccess.value = '密码更新成功';
  passwordData.value = { currentPassword: '', newPassword: '', confirmPassword: '' };
  setTimeout(() => passwordSuccess.value = '', 3000);
});

onChangePasswordError((err) => {
  passwordError.value = err.message || '密码更新失败';
});

// Form handlers
const handleLogin = () => {
  loginError.value = '';
  login({
    input: {
      username: loginData.value.username,
      password: loginData.value.password
    }
  });
};

const handleSignup = () => {
  signupError.value = '';
  
  if (signupData.value.password !== signupData.value.confirmPassword) {
    signupError.value = '两次输入的密码不一致';
    return;
  }

  signup({
    input: {
      username: signupData.value.username,
      password: signupData.value.password
    }
  });
};

const handleChangePassword = () => {
  passwordError.value = '';
  passwordSuccess.value = '';
  
  if (passwordData.value.newPassword !== passwordData.value.confirmPassword) {
    passwordError.value = '两次输入的新密码不一致';
    return;
  }

  changePassword({
    currentPassword: passwordData.value.currentPassword,
    newPassword: passwordData.value.newPassword
  });
};
</script>

<style scoped>
.auth-container {
  max-width: 400px;
  margin: 2rem auto;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.auth-tabs {
  display: flex;
  border-bottom: 1px solid var(--border-color);
}

.auth-tab {
  flex: 1;
  padding: 1rem;
  text-align: center;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1rem;
  color: var(--text-medium);
  transition: all 0.2s ease;
}

.auth-tab:hover {
  background: var(--light-bg);
}

.auth-tab.active {
  color: var(--primary-color);
  font-weight: 600;
  border-bottom: 2px solid var(--primary-color);
}

.auth-form {
  padding: 2rem;
}

.auth-form-content {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

h2 {
  margin: 0 0 1rem;
  color: var(--text-dark);
  text-align: center;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-input {
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 4px;
  font-size: 1rem;
  transition: border-color 0.2s ease;
}

.form-input:focus {
  outline: none;
  border-color: var(--primary-color);
}

.button {
  padding: 0.75rem;
  border-radius: 4px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.button-primary {
  background-color: var(--primary-color);
  color: white;
  border: none;
}

.button-primary:hover {
  background-color: var(--primary-hover);
}

.error-message {
  color: var(--danger-color);
  text-align: center;
  margin: 0.5rem 0 0;
}

.success-message {
  color: var(--primary-color);
  text-align: center;
  margin: 0.5rem 0 0;
}
</style>